import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  CircularProgress,
  Stack,
  Typography,
  Box,
  Divider,
} from "@mui/material";

import { getAdminCategories } from "../../../services/Admin/ProductsAndCategoriesApi";
import {
  addCategoriesToBanner,
  removeCategoriesFromBanner,
} from "../../../services/Admin/AdvertisingBannersApi";

const AddCategoriesToBannerDialog = ({
  open,
  onClose,
  bannerId,
  existingCategoryIds = [],
  onSuccess,
}) => {
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingRemove, setLoadingRemove] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoryGroups, setCategoryGroups] = useState([]);
  const [error, setError] = useState("");

  // ✅ ids الموجودة بالفعل (set) — بنحدثها بعد الإضافة/الحذف
  const [existingSetState, setExistingSetState] = useState(new Set());

  // ✅ اختيار الإضافة (الجديد فقط)
  const [selectedAdd, setSelectedAdd] = useState([]);

  // ✅ اختيار الحذف (من الموجود فقط)
  const [selectedRemove, setSelectedRemove] = useState([]);

  useEffect(() => {
    if (open) {
      setError("");
      setLoadingAdd(false);
      setLoadingRemove(false);

      const initial = new Set(
        (Array.isArray(existingCategoryIds) ? existingCategoryIds : []).map(Number)
      );
      setExistingSetState(initial);

      // الإضافة تبدأ فاضية (الجديد فقط)
      setSelectedAdd([]);

      // الحذف يبدأ فاضي
      setSelectedRemove([]);

      fetchCategories();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bannerId, existingCategoryIds]);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await getAdminCategories();
      const data = res?.success ? res.data : res;
      setCategoryGroups(Array.isArray(data) ? data : []);
    } catch {
      setCategoryGroups([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // ✅ جمع كل ids من الشجرة
  const collectAllCategoryIds = (groups) => {
    const ids = [];
    const walk = (node) => {
      if (!node) return;
      if (node.id !== undefined && node.id !== null) ids.push(Number(node.id));
      const children = node.children_recursive || [];
      if (Array.isArray(children)) children.forEach(walk);
    };
    if (Array.isArray(groups)) groups.forEach(walk);
    return Array.from(new Set(ids));
  };

  const allCategoryIds = useMemo(
    () => collectAllCategoryIds(categoryGroups),
    [categoryGroups]
  );

  const notAddedIds = useMemo(
    () => allCategoryIds.filter((id) => !existingSetState.has(Number(id))),
    [allCategoryIds, existingSetState]
  );

  const addedIds = useMemo(
    () => allCategoryIds.filter((id) => existingSetState.has(Number(id))),
    [allCategoryIds, existingSetState]
  );

  // =========================
  // إضافة (الجديد فقط)
  // =========================
  const toggleAdd = (id) => {
    const nid = Number(id);
    // ماينفعش تضيف شيء موجود
    if (existingSetState.has(nid)) return;

    setSelectedAdd((prev) => {
      const p = Array.isArray(prev) ? prev.map(Number) : [];
      return p.includes(nid) ? p.filter((x) => x !== nid) : [...p, nid];
    });
  };

  const allNotAddedSelected = useMemo(() => {
    const sel = selectedAdd.map(Number);
    return notAddedIds.length > 0 && notAddedIds.every((id) => sel.includes(Number(id)));
  }, [notAddedIds, selectedAdd]);

  const toggleAllAdd = () => {
    if (allNotAddedSelected) setSelectedAdd([]);
    else setSelectedAdd(notAddedIds.map(Number));
  };

  const handleAdd = async () => {
    if (!bannerId) return;
    setError("");

    const newIds = Array.isArray(selectedAdd) ? selectedAdd.map(Number) : [];
    if (newIds.length === 0) {
      setError("يرجى اختيار تصنيف واحد على الأقل للإضافة");
      return;
    }

    setLoadingAdd(true);
    try {
      const res = await addCategoriesToBanner(bannerId, newIds);
      if (res?.success) {
        setExistingSetState((prev) => {
          const s = new Set(prev);
          newIds.forEach((id) => s.add(Number(id)));
          return s;
        });

        // بعد الإضافة: فضّي تحديد الإضافة
        setSelectedAdd([]);

        onSuccess?.();
      } else {
        setError(res?.message_ar || res?.message_en || "فشل إضافة التصنيفات");
      }
    } catch (err) {
      setError(err?.message || "فشل إضافة التصنيفات");
    } finally {
      setLoadingAdd(false);
    }
  };

  // =========================
  // حذف (متعدد)
  // =========================
  const toggleRemove = (id) => {
    const nid = Number(id);
    // ماينفعش تحذف شيء غير موجود
    if (!existingSetState.has(nid)) return;

    setSelectedRemove((prev) => {
      const p = Array.isArray(prev) ? prev.map(Number) : [];
      return p.includes(nid) ? p.filter((x) => x !== nid) : [...p, nid];
    });
  };

  const allAddedSelectedForRemove = useMemo(() => {
    const sel = selectedRemove.map(Number);
    return addedIds.length > 0 && addedIds.every((id) => sel.includes(Number(id)));
  }, [addedIds, selectedRemove]);

  const toggleAllRemove = () => {
    if (allAddedSelectedForRemove) setSelectedRemove([]);
    else setSelectedRemove(addedIds.map(Number));
  };

  const handleRemoveSelected = async () => {
    if (!bannerId) return;
    setError("");

    const idsToRemove = Array.isArray(selectedRemove) ? selectedRemove.map(Number) : [];
    if (idsToRemove.length === 0) {
      setError("يرجى اختيار تصنيف واحد على الأقل للحذف");
      return;
    }

    if (!window.confirm(`هل تريد حذف (${idsToRemove.length}) تصنيف من البانر؟`)) return;

    setLoadingRemove(true);
    try {
      const res = await removeCategoriesFromBanner(bannerId, idsToRemove);
      if (res?.success) {
        setExistingSetState((prev) => {
          const s = new Set(prev);
          idsToRemove.forEach((id) => s.delete(Number(id)));
          return s;
        });

        // بعد الحذف: فضّي تحديد الحذف
        setSelectedRemove([]);

        onSuccess?.();
      } else {
        setError(res?.message_ar || res?.message_en || "فشل حذف التصنيفات");
      }
    } catch (err) {
      setError(err?.message || "حدث خطأ أثناء الحذف");
    } finally {
      setLoadingRemove(false);
    }
  };

  // =========================
  // Render
  // =========================
  const renderCategoryNode = (node, level = 0) => {
    const cid = Number(node.id);
    const isAlreadyAdded = existingSetState.has(cid);

    const checkedAdd = selectedAdd.map(Number).includes(cid);
    const checkedRemove = selectedRemove.map(Number).includes(cid);

    return (
      <Box key={cid} sx={{ pl: level * 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
          {/* ✅ Checkbox للإضافة (الجديد فقط) */}
          <Checkbox
            checked={checkedAdd}
            disabled={isAlreadyAdded} // ممنوع إضافة الموجود
            onChange={() => toggleAdd(cid)}
          />

          <Typography
            variant="body2"
            sx={{ flex: 1, opacity: isAlreadyAdded ? 0.85 : 1 }}
          >
            {node.name_ar || node.name_en}
            {isAlreadyAdded ? " (مضاف)" : ""}
          </Typography>

          {/* ✅ Checkbox للحذف (للموجود فقط) */}
          <Checkbox
            checked={checkedRemove}
            disabled={!isAlreadyAdded} // ممنوع حذف غير الموجود
            onChange={() => toggleRemove(cid)}
          />
        </Box>

        {Array.isArray(node.children_recursive) && node.children_recursive.length > 0 && (
          <Box sx={{ pl: 1 }}>
            {node.children_recursive.map((child) => renderCategoryNode(child, level + 1))}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>إدارة تصنيفات البانر (إضافة / حذف)</DialogTitle>

      <DialogContent>
        {/* Controls */}
        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 1, flexWrap: "wrap" }}>
          <Button size="small" onClick={toggleAllAdd} disabled={categoriesLoading || !notAddedIds.length}>
            {allNotAddedSelected ? "إلغاء تحديد الإضافة" : "تحديد كل الجديد للإضافة"}
          </Button>

          <Button size="small" color="error" onClick={toggleAllRemove} disabled={categoriesLoading || !addedIds.length}>
            {allAddedSelectedForRemove ? "إلغاء تحديد الحذف" : "تحديد كل الموجود للحذف"}
          </Button>
        </Stack>

        <Divider sx={{ mb: 1 }} />

        <Stack direction="row" spacing={2} sx={{ mb: 1, justifyContent: "space-between" }}>
          <Typography variant="caption" color="text.secondary">
            ✅ (يسار) تحديد للإضافة — الجديد فقط
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ✅ (يمين) تحديد للحذف — الموجود فقط
          </Typography>
        </Stack>

        <Stack spacing={2} sx={{ mt: 1 }}>
          {categoriesLoading ? (
            <Typography>جارٍ التحميل...</Typography>
          ) : categoryGroups.length ? (
            categoryGroups.map((root) => (
              <Box key={root.id} sx={{ mb: 1 }}>
                <Typography variant="subtitle2">{root.name_ar || root.name_en}</Typography>
                <Box sx={{ pl: 2, pt: 0.5 }}>{renderCategoryNode(root, 0)}</Box>
              </Box>
            ))
          ) : (
            <Typography color="text.secondary">لا توجد تصنيفات</Typography>
          )}

          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loadingAdd || loadingRemove}>
          إلغاء
        </Button>

        <Box sx={{ flex: 1 }} />

        <Button
          color="error"
          variant="outlined"
          onClick={handleRemoveSelected}
          disabled={loadingAdd || loadingRemove || categoriesLoading}
        >
          {loadingRemove ? <CircularProgress size={18} /> : `حذف المحدد (${selectedRemove.length})`}
        </Button>

        <Button
          variant="contained"
          onClick={handleAdd}
          disabled={loadingAdd || loadingRemove || categoriesLoading}
        >
          {loadingAdd ? <CircularProgress size={18} /> : `إضافة المحدد (${selectedAdd.length})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCategoriesToBannerDialog;
